import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { 
  Activity, 
  Filter, 
  Eye, 
  Download,
  RefreshCw,
  AlertTriangle,
  User,
  Shield,
  Clock,
  Sparkles
} from 'lucide-react';
import { loggingApiService, UserActivity, ActivityFilters } from '../../../../services/loggingApiService';
import { UserSearchAutocomplete } from './UserSearchAutocomplete';
import { useLoggingWebSocket } from '../../../../hooks/useLoggingWebSocket';
import LexMessageRenderer from '../../../../components/lex/LexMessageRenderer';

interface ActivityDetailModalProps {
  activity: UserActivity | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Activity Detail Modal Component
 */
const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({ activity, isOpen, onClose }) => {
  const [lexExpanded, setLexExpanded] = useState(false);
  const [lexLoading, setLexLoading] = useState(false);
  const [lexError, setLexError] = useState<string | null>(null);
  const [lexExplanation, setLexExplanation] = useState<string | null>(null);
  const [lexProvider, setLexProvider] = useState<string | null>(null);
  const [lexModel, setLexModel] = useState<string | null>(null);

  useEffect(() => {
    // Reset when switching activities or reopening modal
    setLexExpanded(false);
    setLexLoading(false);
    setLexError(null);
    setLexExplanation(null);
    setLexProvider(null);
    setLexModel(null);
  }, [activity?.id, isOpen]);

  const runLexExplain = async () => {
    if (!activity) return;
    setLexExpanded(true);
    if (lexLoading || lexExplanation) return;

    setLexLoading(true);
    setLexError(null);
    try {
      const res = await loggingApiService.explainWithLex({
        type: 'user_activity',
        payload: activity
      });
      setLexExplanation(res.explanation);
      setLexProvider(res.provider);
      setLexModel(res.model);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to generate Lex explanation');
    } finally {
      setLexLoading(false);
    }
  };

  if (!isOpen || !activity) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border border-gray-200 dark:border-gray-700">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Activity Details</h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runLexExplain}
                  disabled={lexLoading}
                  className="flex items-center space-x-2"
                >
                  <Sparkles className={`w-4 h-4 ${lexLoading ? 'animate-pulse' : ''}`} />
                  <span>Lex</span>
                </Button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timestamp</label>
                  <p className="text-sm text-gray-900 dark:text-white">{new Date(activity.timestamp).toLocaleString()}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User Information</label>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 dark:text-white">{activity.userName}</span>
                      {activity.isAttorney && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-lex-accent-soft text-lex-accent">
                          Attorney
                        </span>
                      )}
                      {(activity.userClassification === 'Administrator' || activity.userClassification === 'Admin') && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100">
                          Admin
                        </span>
                      )}
                    </div>
                    {activity.userEmail && (
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        📧 {activity.userEmail}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      ID: {activity.userId}
                    </div>
                    <div className="flex items-center space-x-4 text-xs">
                      <span className="flex items-center space-x-1">
                        <span className="font-medium">Classification:</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          activity.userClassification === 'Attorney' ? 'bg-lex-accent-soft text-lex-accent' :
                          activity.userClassification === 'Admin' ? 'bg-purple-50 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                          activity.userClassification === 'Client' ? 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300' :
                          'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {activity.userClassification}
                        </span>
                      </span>
                      {activity.dmsRole && (
                        <span className="flex items-center space-x-1">
                          <span className="font-medium">Role:</span>
                          <span className="text-gray-600 dark:text-gray-300">{activity.dmsRole}</span>
                        </span>
                      )}
                      <span className="flex items-center space-x-1">
                        <span className="font-medium">Access Level:</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          (activity.userClassification === 'Administrator' || activity.userClassification === 'Admin') ? 'bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-300' :
                          activity.userClassification === 'Attorney' ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                          'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300'
                        }`}>
                          {activity.userClassification === 'Administrator' || activity.userClassification === 'Admin' ? 'High' :
                           activity.userClassification === 'Attorney' ? 'Medium' : 'Standard'}
                        </span>
                      </span>
                    </div>
                    {activity.firmName && (
                      <div className="text-xs text-gray-600 dark:text-gray-300">
                        🏢 {activity.firmName}
                      </div>
                    )}
                    {activity.lastLoginAt && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Last login: {new Date(activity.lastLoginAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Activity Type</label>
                  <Badge variant="secondary">{activity.activityType}</Badge>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Risk Score</label>
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      activity.riskScore >= 70 ? 'bg-red-400' :
                      activity.riskScore >= 40 ? 'bg-yellow-400' : 'bg-green-400'
                    }`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{activity.riskScore}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IP Address</label>
                  <p className="text-sm text-gray-900 dark:text-white">{activity.ipAddress || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                  <p className="text-sm text-gray-900 dark:text-white">{activity.activityCategory}</p>
                </div>
              </div>
              
              {activity.endpoint && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Endpoint</label>
                  <p className="text-sm text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-gray-700 p-2 rounded">
                    {activity.httpMethod} {activity.endpoint}
                  </p>
                </div>
              )}
              
              {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Metadata</label>
                  <pre className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white p-3 rounded overflow-auto max-h-40">
                    {JSON.stringify(activity.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {lexExpanded && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Lex Explanation
                    </label>
                    {lexProvider && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {lexProvider}{lexModel ? ` • ${lexModel}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {lexLoading ? (
                      <div className="text-sm text-gray-600 dark:text-gray-300">Lex is analyzing this…</div>
                    ) : lexError ? (
                      <div className="text-sm text-red-600 dark:text-red-400">{lexError}</div>
                    ) : (
                      <LexMessageRenderer
                        content={lexExplanation || 'No explanation returned.'}
                        role="assistant"
                        enableLegalFormatting={false}
                        enableSmartEmojis={true}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 dark:border-gray-600">
            <Button onClick={onClose} className="w-full sm:w-auto">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * User Activities Panel Component
 * 
 * Displays live user activity monitoring with advanced filtering
 */
export const UserActivitiesPanel: React.FC = () => {
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ActivityFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<UserActivity | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Real-time updates from WebSocket
  const { isConnected, lastActivity } = useLoggingWebSocket();

  // Load activities with current filters
  const loadActivities = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await loggingApiService.getUserActivities({
        ...filters,
        page,
        limit: pagination.limit
      });
      
      setActivities(response.activities);
      setPagination(prev => ({
        ...prev,
        page,
        total: response.total,
        totalPages: response.totalPages
      }));
    } catch (error) {
      console.error('Failed to load activities:', error);
      setError(error instanceof Error ? error.message : 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Initial load
  useEffect(() => {
    loadActivities(1);
  }, [filters]);

  // Handle real-time activity updates
  useEffect(() => {
    if (lastActivity && pagination.page === 1) {
      setActivities(prev => {
        if (!prev || !Array.isArray(prev)) return [lastActivity];
        return [lastActivity, ...prev.slice(0, pagination.limit - 1)];
      });
    }
  }, [lastActivity, pagination.page, pagination.limit]);

  const handleFilterChange = (key: keyof ActivityFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };


  const clearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportActivities = async () => {
    try {
      // Note: This would need to be implemented in the API service
      console.log('Export functionality would be implemented here');
    } catch (error) {
      console.error('Failed to export activities:', error);
    }
  };

  const viewActivityDetail = (activity: UserActivity) => {
    setSelectedActivity(activity);
    setShowDetailModal(true);
  };

  const getActivityTypeColor = (activityType: string) => {
    const colorMap: Record<string, string> = {
      'user_login': 'bg-lex-accent-soft text-lex-accent',
      'document_access': 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
      'matter_creation': 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400',
      'client_creation': 'bg-lex-accent-soft text-lex-accent',
      'document_upload': 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400',
      'report_generation': 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400'
    };
    return colorMap[activityType] || 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-lex-accent" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Live User Activities
            </h3>
          </div>
          
          {/* Real-time status indicator */}
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-400 animate-pulse' : 'bg-gray-400'
            }`} />
            <span className="text-xs text-gray-500">
              {isConnected ? 'Live Updates' : 'Disconnected'}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {Object.keys(filters).some(key => filters[key as keyof ActivityFilters]) && (
              <Badge variant="secondary" className="ml-1">
                {Object.keys(filters).filter(key => filters[key as keyof ActivityFilters]).length}
              </Badge>
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadActivities(pagination.page)}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={exportActivities}
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* User Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User Name
              </label>
              <UserSearchAutocomplete
                value={filters.userName || ''}
                onChange={(name, userId) => {
                  // Always use userName filter (backend handles multiple user IDs)
                  handleFilterChange('userName', name);
                  handleFilterChange('userId', '');
                }}
                placeholder="Search by name or email..."
              />
            </div>

            {/* Activity Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Activity Type
              </label>
              <select
                value={filters.activityType || ''}
                onChange={(e) => handleFilterChange('activityType', e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
              >
                <option value="">All Types</option>
                <option value="user_login">User Login</option>
                <option value="user_deactivation">User Deactivation</option>
                <option value="user_reactivation">User Reactivation</option>
                <option value="document_access">Document Access</option>
                <option value="document_version_access">Document Version Access</option>
                <option value="document_analytics">Document Analytics</option>
                <option value="document_update">Document Update</option>
                <option value="client_access">Client Access</option>
                <option value="client_update">Client Update</option>
                <option value="matter_access">Matter Access</option>
                <option value="matter_update">Matter Update</option>
                <option value="matter_progress_check">Matter Progress Check</option>
                <option value="admin_access">Admin Access</option>
                <option value="group_management_access">Group Management</option>
                <option value="user_management_access">User Management Access</option>
              </select>
            </div>

                         {/* Risk Score Filter */}
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                 Risk Level
               </label>
               <select
                 value={
                   filters.riskScoreMin === 0 && filters.riskScoreMax === 30 ? 'low' :
                   filters.riskScoreMin === 31 && filters.riskScoreMax === 70 ? 'medium' :
                   filters.riskScoreMin === 71 && filters.riskScoreMax === 100 ? 'high' : ''
                 }
                 onChange={(e) => {
                   const level = e.target.value;
                   const riskRanges = {
                     low: { min: 0, max: 30 },
                     medium: { min: 31, max: 70 },
                     high: { min: 71, max: 100 }
                   };
                   
                   if (level && riskRanges[level as keyof typeof riskRanges]) {
                     const range = riskRanges[level as keyof typeof riskRanges];
                     setFilters(prev => ({
                       ...prev,
                       riskScoreMin: range.min,
                       riskScoreMax: range.max
                     }));
                   } else {
                     setFilters(prev => ({
                       ...prev,
                       riskScoreMin: undefined,
                       riskScoreMax: undefined
                     }));
                   }
                 }}
                 className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
               >
                 <option value="">All Risk Levels</option>
                 <option value="low">Low Risk (0-30)</option>
                 <option value="medium">Medium Risk (31-70)</option>
                 <option value="high">High Risk (71-100)</option>
               </select>
             </div>

                         {/* Time Period Filter */}
             <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                 Time Period
               </label>
               <select
                 value={
                   !filters.startDate ? '' :
                   filters.startDate && new Date(filters.startDate).getTime() > new Date().getTime() - 60 * 60 * 1000 ? '1h' :
                   filters.startDate && new Date(filters.startDate).getTime() > new Date().getTime() - 24 * 60 * 60 * 1000 ? '24h' :
                   filters.startDate && new Date(filters.startDate).getTime() > new Date().getTime() - 7 * 24 * 60 * 60 * 1000 ? '7d' :
                   filters.startDate && new Date(filters.startDate).getTime() > new Date().getTime() - 30 * 24 * 60 * 60 * 1000 ? '30d' : ''
                 }
                 onChange={(e) => {
                   const range = e.target.value;
                   const now = new Date();
                   let startDate: Date | undefined;
                   
                   switch (range) {
                     case '1h': startDate = new Date(now.getTime() - 60 * 60 * 1000); break;
                     case '24h': startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
                     case '7d': startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
                     case '30d': startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
                   }
                   
                   setFilters(prev => ({
                     ...prev,
                     startDate: startDate?.toISOString(),
                     endDate: range ? now.toISOString() : undefined
                   }));
                 }}
                 className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
               >
                 <option value="">All Time</option>
                 <option value="1h">Last Hour</option>
                 <option value="24h">Last 24 Hours</option>
                 <option value="7d">Last 7 Days</option>
                 <option value="30d">Last 30 Days</option>
               </select>
             </div>
          </div>

          {/* Enhanced Legal Analytics Filters */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Legal Analytics Filters</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              
              {/* User Classification Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  User Type
                </label>
                <select
                  value={filters.userClassification || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters(prev => ({ 
                      ...prev, 
                      userClassification: value === '' ? undefined : value as 'Attorney' | 'Staff' | 'Admin' | 'Client'
                    }));
                  }}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
                >
                  <option value="">All User Types</option>
                  <option value="Attorney">Attorneys</option>
                  <option value="Staff">Staff</option>
                  <option value="Admin">Administrators</option>
                  <option value="Client">Clients</option>
                </select>
              </div>

              {/* Attorney Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Attorney Activities
                </label>
                <select
                  value={filters.isAttorney === undefined ? '' : filters.isAttorney.toString()}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    isAttorney: e.target.value === '' ? undefined : e.target.value === 'true'
                  }))}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
                >
                  <option value="">All Users</option>
                  <option value="true">Attorney Only</option>
                  <option value="false">Non-Attorney</option>
                </select>
              </div>

              {/* Permission Level Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Permission Level
                </label>
                <select
                  value={filters.permissionLevel || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters(prev => ({ 
                      ...prev, 
                      permissionLevel: value === '' ? undefined : value as 'High' | 'Medium' | 'Low'
                    }));
                  }}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
                >
                  <option value="">All Permission Levels</option>
                  <option value="High">High (Admin)</option>
                  <option value="Medium">Medium (Attorney)</option>
                  <option value="Low">Low (Staff)</option>
                </select>
              </div>

              {/* DMS Role Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  DMS Role
                </label>
                <select
                  value={filters.dmsRole || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters(prev => ({ 
                      ...prev, 
                      dmsRole: value === '' ? undefined : value as 'Attorney' | 'Paralegal' | 'Admin' | 'Client'
                    }));
                  }}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
                >
                  <option value="">All DMS Roles</option>
                  <option value="Attorney">Attorney</option>
                  <option value="Paralegal">Paralegal</option>
                  <option value="Admin">Admin</option>
                  <option value="Client">Client</option>
                </select>
              </div>

              {/* Activity Risk Context Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Activity Context
                </label>
                <select
                  value={filters.activityRiskContext || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilters(prev => ({ 
                      ...prev, 
                      activityRiskContext: value === '' ? undefined : value as 'Attorney_Action' | 'Staff_Action' | 'Admin_Action' | 'Client_Action'
                    }));
                  }}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:border-lex-accent focus:ring-lex-accent"
                >
                  <option value="">All Activity Contexts</option>
                  <option value="Attorney_Action">Attorney Actions</option>
                  <option value="Staff_Action">Staff Actions</option>
                  <option value="Admin_Action">Admin Actions</option>
                  <option value="Client_Action">Client Actions</option>
                </select>
              </div>

            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="flex items-center space-x-2"
            >
              <span>Clear All Filters</span>
            </Button>
            
            <div className="text-sm text-gray-500">
              Showing {activities.length} of {pagination.total} activities
            </div>
          </div>
        </div>
      )}

      {/* Activities Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
            <span className="ml-3 text-gray-600">Loading activities...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
              <p className="text-red-600 font-medium">Failed to load activities</p>
              <p className="text-gray-500 text-sm mt-1">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadActivities(pagination.page)}
                className="mt-3"
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Activity className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No activities found</p>
              <p className="text-gray-500 text-sm mt-1">
                {Object.keys(filters).some(key => filters[key as keyof ActivityFilters]) 
                  ? 'Try adjusting your filters'
                  : 'Activities will appear here when users perform actions'
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>Time</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>User</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4" />
                        <span>Risk</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {activities.map((activity) => (
                    <tr 
                      key={activity.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        <div>
                          <div className="font-medium">
                            {new Date(activity.timestamp).toLocaleDateString()}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {new Date(activity.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        <div className="space-y-1">
                          <div className="font-medium flex items-center space-x-2">
                            <span>{activity.userName || 'Unknown User'}</span>
                            {activity.isAttorney && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-lex-accent-soft text-lex-accent">
                                Attorney
                              </span>
                            )}
                            {activity.userClassification === 'Administrator' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100">
                                Admin
                              </span>
                            )}
                          </div>
                          {activity.userEmail && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {activity.userEmail}
                            </div>
                          )}
                          <div className="flex items-center space-x-2 text-xs">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              activity.userClassification === 'Attorney' ? 'bg-lex-accent-soft text-lex-accent' :
                              activity.userClassification === 'Admin' ? 'bg-purple-50 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                              activity.userClassification === 'Client' ? 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300' :
                              'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {activity.userClassification}
                            </span>
                            {activity.dmsRole && (
                              <span className="text-gray-500 dark:text-gray-400">
                                ({activity.dmsRole})
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {activity.activityDescription || activity.activityType.replace('_', ' ')}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActivityTypeColor(activity.activityType)}`}>
                              {activity.activityCategory || 'General'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${
                              activity.riskColor === 'red' ? 'bg-red-400' :
                              activity.riskColor === 'yellow' ? 'bg-yellow-400' : 'bg-green-400'
                            }`} />
                            <span className={`text-sm font-medium ${
                              activity.riskColor === 'red' ? 'text-red-600 dark:text-red-400' :
                              activity.riskColor === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-green-600 dark:text-green-400'
                            }`}>
                              {activity.riskLevel || 'Low Risk'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {activity.riskDescription || 'Normal activity'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {activity.ipAddress || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewActivityDetail(activity)}
                          className="flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <Button
                    variant="outline"
                    onClick={() => loadActivities(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => loadActivities(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing{' '}
                      <span className="font-medium">
                        {((pagination.page - 1) * pagination.limit) + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{pagination.total}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <Button
                        variant="outline"
                        onClick={() => loadActivities(pagination.page - 1)}
                        disabled={pagination.page <= 1 || loading}
                        className="rounded-l-md"
                      >
                        Previous
                      </Button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const pageNum = pagination.page <= 3 
                          ? i + 1 
                          : pagination.page >= pagination.totalPages - 2 
                            ? pagination.totalPages - 4 + i 
                            : pagination.page - 2 + i;
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pagination.page === pageNum ? "default" : "outline"}
                            onClick={() => loadActivities(pageNum)}
                            disabled={loading}
                            className="border-l-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      
                      <Button
                        variant="outline"
                        onClick={() => loadActivities(pagination.page + 1)}
                        disabled={pagination.page >= pagination.totalPages || loading}
                        className="rounded-r-md border-l-0"
                      >
                        Next
                      </Button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Activity Detail Modal */}
      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedActivity(null);
        }}
      />
    </div>
  );
}; 
